import { Mail, Phone, Calendar, CheckSquare, Radar, Home } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { TimelineEntry } from '@shared/schemas/timeline-entry';
import { parseBareCalendarDay } from '../../lib/calendarDay';

/**
 * Lucide icon for each timeline `type`. Keys are the SF `type` values we
 * expect from staging (case-insensitive match applied by `iconFor`).
 *
 * Staging sampling (bd-ugnf) will confirm the real distribution — today
 * we use the sensible defaults resolved in the bd-1kg6 prompt:
 *
 *   Email → Mail, Call → Phone, Meeting → Calendar, Task → CheckSquare.
 *
 * Unknown values fall through to the kind-default (see `iconFor`).
 */
const TYPE_ICON_MAP: Record<string, LucideIcon> = {
  email: Mail,
  call: Phone,
  meeting: Calendar,
  task: CheckSquare,
};

/**
 * Accent colors per entry `kind`. Uses Clay brand palette tokens so the
 * icon chip echoes the activity type without adding raw hex values.
 */
export const KIND_ACCENT: Record<TimelineEntry['kind'], { bg: string; fg: string }> = {
  event: { bg: 'bg-ube-50', fg: 'text-ube-700' },
  task: { bg: 'bg-tangerine-50', fg: 'text-tangerine-700' },
  // Signals (GTMOPS-2162) share the ube accent this file already gives
  // events. The prototype recolors event → nightshade so signal owns ube
  // alone; that recolor is a restyle, so it belongs to the U6 frontend
  // unit, not to this contract-widening pass.
  signal: { bg: 'bg-ube-50', fg: 'text-ube-700' },
  // Workspace creates/joins (Bucket B U9) — the prototype's matcha `⌂`
  // accent, same token pair the Flow AccountTimeline uses.
  workspace_created: { bg: 'bg-bg-green-light', fg: 'text-content-success' },
  workspace_join: { bg: 'bg-bg-green-light', fg: 'text-content-success' },
};

/** Pick the best lucide icon for an entry. */
export function iconFor(entry: TimelineEntry): LucideIcon {
  // Signals name their category in `signalType`/`signalSubtype`; tasks and
  // events use `type`/`subtype`; workspace rows have no type field at all.
  const candidates =
    entry.kind === 'signal' ? [entry.signalType, entry.signalSubtype]
    : entry.kind === 'workspace_created' || entry.kind === 'workspace_join' ? []
    : [entry.type, entry.subtype];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const hit = TYPE_ICON_MAP[candidate.toLowerCase()];
    if (hit) return hit;
  }
  // Kind defaults: events are calendar-like, tasks are check-mark-like,
  // signals get Radar (the icon both Signals surfaces already use),
  // workspace rows get the prototype's house glyph.
  if (entry.kind === 'signal') return Radar;
  if (entry.kind === 'workspace_created' || entry.kind === 'workspace_join') return Home;
  return entry.kind === 'event' ? Calendar : CheckSquare;
}

/**
 * The row title for any entry kind.
 *
 * Tasks/events title on `subject`; signals title on `eventName ?? name` —
 * the same precedence the Signals surfaces use (`eventName` is the human
 * `Event_Name__c`, `name` the autonumber-ish SF standard Name). Workspace
 * rows title on "Workspace created · {name}" / "{contactName} joined
 * {name}" (contactName is frequently null — see `TimelineWorkspaceCreated/
 * JoinEntrySchema` — so the join title falls back to "Someone joined
 * {name}" rather than rendering blank). Returns `null` when nothing is
 * titled so each surface picks its own placeholder copy rather than
 * inheriting one.
 */
export function timelineEntryTitle(entry: TimelineEntry): string | null {
  if (entry.kind === 'signal') return entry.eventName ?? entry.name;
  if (entry.kind === 'workspace_created' || entry.kind === 'workspace_join') {
    const workspace = entry.workspaceName ?? 'a workspace';
    if (entry.kind === 'workspace_created') {
      return entry.workspaceName ? `Workspace created · ${entry.workspaceName}` : 'Workspace created';
    }
    return entry.contactName ? `${entry.contactName} joined ${workspace}` : `Someone joined ${workspace}`;
  }
  return entry.subject;
}

/**
 * Effective ISO date string for display formatting.
 *
 * Equal to the server-emitted `effectiveAt` (`activityDate` for tasks,
 * `startDateTime` for events). Re-exported as a function rather than
 * inlining the field access because callers historically called this
 * helper and the indirection makes future shape changes one-edit.
 */
export function effectiveDateIso(entry: TimelineEntry): string | null {
  return entry.effectiveAt;
}

/**
 * Short, relative time label — "2h ago", "Tomorrow", "Mar 12".
 *
 * Copy-adapted from `designs/timeline-tab-options.html:138-152` (Option 1
 * `fmtTime`). The `NOW` constant becomes an argument so the function is
 * testable and the hook can pass a stable value.
 */
export function formatRelativeTime(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffSec = (now.getTime() - d.getTime()) / 1000;
  if (diffSec < 0) {
    const secondsAhead = -diffSec;
    if (secondsAhead < 86400) return 'Today';
    if (secondsAhead < 172_800) return 'Tomorrow';
    if (secondsAhead < 604_800) return d.toLocaleDateString('en-US', { weekday: 'short' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 604_800) return `${Math.floor(diffSec / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * A bare `YYYY-MM-DD` is a calendar date, so it parses at LOCAL midnight, the
 * same rule as `daysSince` / `formatSourceDate` in `open-loops/recentLoops.ts`.
 * `new Date('YYYY-MM-DD')` is UTC midnight, which skews the age by up to a day
 * in any zone away from UTC. Full timestamps are real instants and parse as-is.
 */
function parseAgeInstant(iso: string): Date {
  return parseBareCalendarDay(iso) ?? new Date(iso);
}

/**
 * Compact relative age that keeps a relative token across the whole
 * range — "3d ago", "3w ago", "2mo ago", "1y ago" — unlike
 * `formatRelativeTime`, which falls back to a bare date past a week.
 *
 * Sibling of `formatRelativeTime` (same module, same `(iso, now)`
 * signature) rather than a new copy, so the Signals surface can show
 * "Jul 30, 2026 · 3d ago" for a signal of any age in its 90-day window
 * without the >7d rows degrading to a redundant second date. Returns
 * `null` for an unparseable/empty ISO so callers can omit the segment.
 */
export function formatCompactAge(iso: string | null, now: Date): string | null {
  if (!iso) return null;
  const d = parseAgeInstant(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffSec = Math.max(0, (now.getTime() - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (days < 30) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (days < 365) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

/**
 * Human-readable `people` list for a row's second line. Events use
 * `attendees[].name`; tasks fall back to `contactName` / `ownerName`.
 */
export function peopleFor(entry: TimelineEntry): string[] {
  // Signals carry no people in the timeline projection (the mirror's
  // contactId/contactName are deliberately not on the wire entry — see
  // `TimelineSignalEntrySchema`), so the second line is empty for them.
  if (entry.kind === 'signal') return [];
  if (entry.kind === 'event') {
    return entry.attendees
      .map(a => a.name)
      .filter((n): n is string => Boolean(n));
  }
  // Workspace rows have no `ownerName` field — just the (often-null)
  // contact.
  if (entry.kind === 'workspace_created' || entry.kind === 'workspace_join') {
    return entry.contactName ? [entry.contactName] : [];
  }
  const names: string[] = [];
  if (entry.contactName) names.push(entry.contactName);
  if (entry.ownerName) names.push(entry.ownerName);
  return names;
}

/**
 * Staleness label for the "Synced Xh ago" chip — returns `null` when the
 * cache is fresh (< 6h old) so the header renders an empty spacer.
 */
export function stalenessLabel(lastSyncedAt: string | null, now: Date): string | null {
  if (!lastSyncedAt) return null;
  const synced = new Date(lastSyncedAt);
  if (Number.isNaN(synced.getTime())) return null;
  const hoursAgo = (now.getTime() - synced.getTime()) / 3_600_000;
  if (hoursAgo < 6) return null;
  return `${Math.round(hoursAgo)}h ago`;
}
